
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import FbxQrScanner from '@/components/fbx-qr-scanner.vue';
import ManualAttachCard from '@/components/ManualAttachCard.vue';
import SvgXMark from '@/components/Icons/SvgXMark.vue';
import CameraBegging from '@/components/CameraBegging.vue';
import InvalidQRConfirmation from '@/components/InvalidQRConfirmation.vue';
import useCameraPermission from '@/shared/composables/useCameraPermission';
import usePermissionAlert from '@/shared/composables/usePermissionAlert';
import {
    extractScannedTrailerId,
    isTrailerSolutionsQrUrl,
} from '@/shared/trailerQrScan';

export default defineComponent({
    components: {
        FbxQrScanner,
        ManualAttachCard,
        SvgXMark,
        CameraBegging,
        InvalidQRConfirmation,
    },
    props: {
        isScannerPaused: Boolean,
    },
    emits: ['onCloseScanner', 'onManualAttach', 'onScanned', 'onScanError'],
    data(): {
        isScannerReady: boolean;
        scannerError: boolean;
        showCameraBegging: boolean;
        displayInvalidQrOverlay: boolean;
    } {
        return {
            isScannerReady: false,
            scannerError: false,
            showCameraBegging: true,
            displayInvalidQrOverlay: false,
        };
    },
    setup() {
        const { isCameraPermissionGranted, setIsCameraShared } =
            useCameraPermission();
        const { setPermissionAlert } = usePermissionAlert();

        return {
            isCameraPermissionGranted,
            setIsCameraShared,
            setPermissionAlert,
        };
    },
    computed: {
        ...mapGetters({
            deviceInfo: 'getDeviceInfo',
        }),
        cameraBeggingEnabled() {
            return this.showCameraBegging && !this.isCameraPermissionGranted;
        },
        actionsEnabled() {
            return this.isScannerReady || this.scannerError;
        },
        isCameraPaused() {
            return this.isScannerPaused || this.displayInvalidQrOverlay;
        },
    },
    methods: {
        showConfirmationModal() {
            this.displayInvalidQrOverlay = true;
        },
        closeConfirmationModal() {
            this.displayInvalidQrOverlay = false;
        },
        onScanned(qrUrl: URL) {
            if (!isTrailerSolutionsQrUrl(qrUrl)) {
                this.showConfirmationModal();
                return;
            }

            this.$emit('onScanned', extractScannedTrailerId(qrUrl));
        },
        onScannerReady(isReady: boolean) {
            this.setIsCameraShared(true);
            this.isScannerReady = isReady;
        },
        onScannerError() {
            this.setCameraUnavailableAndWarn();

            this.scannerError = true;
            this.$emit('onScanError');
        },
        setCameraUnavailableAndWarn() {
            this.setIsCameraShared(false);

            this.setPermissionAlert();
        },
    },
});
